// @flow

/* Based on https://github.com/apollographql/apollo-client/issues/3540#issuecomment-410911223 */

import { ApolloClient } from 'apollo-client';
import { compact, flatten, map, values } from 'lodash';

export const isObservableQueryRefetchable = (observableQuery: any): boolean =>
  observableQuery.options.fetchPolicy !== 'cache-only';

export const getObservableQueriesByName = (
  client: ApolloClient<any>,
  queryName: string
): Array<Object> => {
  const queryManager: any = client.queryManager;

  const { queries } = queryManager;
  const observableQueries = [...queries.values()]
    .map(query => (query ? query.observableQuery : null))
    .filter(
      observableQuery =>
        observableQuery && observableQuery.queryName === queryName
    );

  return observableQueries;
};

export const getAllObservableQueries = (
  client: ApolloClient<any>
): Array<Object> => {
  const queryManager: any = client.queryManager;
  const { queries, queryIdsByName } = queryManager;
  const queryIds = flatten(values(queryIdsByName));

  return map(
    queryIds,
    (queryId: string) => queries.get(queryId).observableQuery
  );
};

export const refetchObservableQueries = (
  refetchedQueries: Array<Object>
): Promise<any> => {
  const promises = compact(
    map(refetchedQueries, (observableQuery: any) =>
      isObservableQueryRefetchable(observableQuery)
        ? observableQuery.refetch()
        : null
    )
  );

  return Promise.all(promises);
};

// We're re-writing QueryManager refetchQueryByName to be less brittle:
// https://github.com/apollographql/apollo-client/blob/88a77511467b2735e841df86073ee3af51e88eec/src/core/QueryManager.ts#L1004
export const refetchQueryByName = (
  client: ApolloClient<any>,
  queryName: string
): Promise<any> => {
  const refetchedQueries = getObservableQueriesByName(client, queryName);

  return refetchObservableQueries(refetchedQueries);
};

export const refetchQueriesByName = (
  client: ApolloClient<any>,
  queryNames: Array<string>
): Promise<any> =>
  Promise.all(
    map(queryNames, (queryName: string) =>
      refetchQueryByName(client, queryName)
    )
  );

export const refetchAllQueries = (client: ApolloClient<any>): Promise<any> => {
  const refetchedQueries = getAllObservableQueries(client);

  return refetchObservableQueries(refetchedQueries);
};
